// src/components/agent/PropertyDetails.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ChevronLeft, Map, Truck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { agentsAPI } from '../../services/api';
import { getDisplayStatus, hideImported, getPropertyBillPrimary, getPropertyBillSecondary } from '../../utils/displayName';

const PropertyDetails = () => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const { billNo } = useParams();
	const [loading, setLoading] = useState(true);
	const [billData, setBillData] = useState(null);
	const [showMap, setShowMap] = useState(false);

	const fetchBillDetails = async () => {
		try {
			setLoading(true);
			if (!user?.id) {
				toast.error('Agent not authenticated');
				navigate('/');
				return;
			}
			if (!billNo) {
				toast.error('Bill number is required');
				return;
			}
			// The backend accepts both valuationNo and billNo
			const data = await agentsAPI.getBillDetails(user.id, decodeURIComponent(billNo));
			setBillData(data);
		} catch (error) {
			const errorMessage = error.response?.data?.message || error.message || 'Failed to load bill details';
			toast.error(errorMessage);
			console.error('Error fetching bill details:', error);
			// Navigate back if bill not found
			if (error.response?.status === 404) {
				setTimeout(() => navigate('/agent/deliver-bills'), 2000);
			}
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (user && billNo) {
			fetchBillDetails();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user, billNo]);

	if (loading) {
		return (
			<div className='h-full flex items-center justify-center'>
				<p className='text-white/60'>Loading...</p>
			</div>
		);
	}

	if (!billData) {
		return (
			<div className='h-full flex items-center justify-center'>
				<p className='text-white/60'>Bill not found</p>
			</div>
		);
	}

	// Determine treatment from backend: Treatment 2 = full info (phone, location, property details)
	const isTreatment2 = billData.treatmentCategory === 'TREATMENT_2';
	// Show map only when we have GPS coordinates (and Treatment 2 gets them from backend)
	const hasLocation = billData.gpsLat != null && billData.gpsLng != null;

	return (
		<div className='h-full flex flex-col overflow-hidden'>
			<header className='p-6 bg-[#001E3C] flex items-center justify-between border-b border-white/10'>
				<button
					type='button'
					onClick={(e) => {
						e.preventDefault();
						navigate('/agent/deliver-bills');
					}}
					className='glass-card p-3 rounded-xl'>
					<ChevronLeft className='w-5 h-5 text-white' />
				</button>
				<h2 className='text-xl font-black uppercase text-white'>
					Bill Details
				</h2>
				<div className='w-10' /> {/* Spacer for centering */}
			</header>

			<div className='flex-1 overflow-y-auto p-6 no-scrollbar text-white'>
				<div className='glass-card p-6 mb-6'>
					<div className='flex justify-between items-start mb-4'>
						<div>
							<p className='font-mono font-bold text-lg mb-1'>
								{getPropertyBillPrimary(billData)}
							</p>
							{getPropertyBillSecondary(billData) && (
								<p className='text-xs text-white/50 font-mono mb-1'>
									Valuation: {getPropertyBillSecondary(billData)}
								</p>
							)}
							{/* <h3 className='text-xl font-bold text-white '>
								{billData.streetName} {billData.houseNumber}
							</h3> */}
						</div>
						<span className={`status-badge status-${(billData.status && billData.status.toLowerCase() !== 'imported' ? billData.status : 'pending')}`}>
							{getDisplayStatus(billData.status)}
						</span>
					</div>

					<div className='space-y-4'>
						{/* Owner Information (prop_owner = Name, mobile_number = Phone from Excel) */}
						<div>
							<h4 className='font-bold mb-2 text-white'>Owner Information</h4>
							<div className='space-y-2'>
								<p className='text-sm text-white'>
									Name : <span className='font-bold'>{hideImported(billData.ownerName || billData.owner_name, 'Not provided')}</span>
								</p>
								{isTreatment2 && (
									<p className='text-sm text-white'>
										Phone: <span className='font-bold'>{hideImported(billData.ownerPhone || billData.owner_phone, 'Not provided')}</span>
									</p>
								)}
								{isTreatment2 && billData.ownerGhanaCard && (
									<p className='text-sm text-white'>
										Ghana Card: <span className='font-bold'>{hideImported(billData.ownerGhanaCard)}</span>
									</p>
								)}
							</div>
						</div>

						{/* Bill Details: arrears from DB (import: total_due - current_amount; fallback rateable_value for legacy rows) */}
						<div>
							<h4 className='font-bold mb-2 text-white'>Bill Details</h4>
							<div className='space-y-2'>
								<div className='flex justify-between'>
									<span className='text-sm text-white/60'>Current amount:</span>
									<span className='font-bold text-white'>₵{(Number(billData.billAmount ?? billData.currentAmount ?? billData.current_amount) || 0).toLocaleString()}</span>
								</div>
								<div className='flex justify-between'>
									<span className='text-sm text-white/60'>Arrears:</span>
									<span className='font-bold text-[#FF1744]'>₵{(Number(billData.arrears ?? billData.rateableValue) || 0).toLocaleString()}</span>
								</div>
								<div className='flex justify-between border-t border-white/20 pt-2 mt-2'>
									<span className='text-sm font-bold text-white'>Amount payable:</span>
									<span className='font-bold text-[#00F5FF]'>₵{(Number(billData.totalDue ?? billData.total_due) || 0).toLocaleString()}</span>
								</div>
							</div>
						</div>

						{/* Property Information - Only show for Treatment 2 or if needed */}
						{isTreatment2 && (
							<div>
								<h4 className='font-bold mb-2 text-white'>Property Information</h4>
								<div className='grid grid-cols-2 gap-4 text-sm'>
									<div>
										<p className='text-white/60'>Revenue Item:</p>
										<p className='font-bold text-white'>{hideImported(billData.revenueItem, 'Property Tax')}</p>
									</div>
									<div>
										<p className='text-white/60'>Due Date:</p>
										<p className='font-bold text-white'>
											{billData.dueDate ? new Date(billData.dueDate).toLocaleDateString() : 'N/A'}
										</p>
									</div>
									{billData.blockNo && (
										<div>
											<p className='text-white/60'>Block No:</p>
											<p className='font-bold text-white'>{hideImported(billData.blockNo)}</p>
										</div>
									)}
									{billData.divisionNo && (
										<div>
											<p className='text-white/60'>Division No:</p>
											<p className='font-bold text-white'>{hideImported(billData.divisionNo)}</p>
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Action Buttons */}
				<div className='grid grid-cols-2 gap-4 mb-6'>
					{/* Location Button */}
					<button
						type='button'
						onClick={(e) => {
							e.preventDefault();
							if (hasLocation) {
								setShowMap(true);
							} else {
								toast.info('Location information not available for your account');
							}
						}}
						className={`glass-card p-4 flex flex-col items-center gap-2 text-white ${
							!hasLocation ? 'opacity-50 cursor-not-allowed' : ''
						}`}>
						<Map className={`w-6 h-6 ${hasLocation ? 'text-[#00F5FF]' : 'text-white/40'}`} />
						<span className='text-sm font-bold'>Location</span>
					</button>
					{billData.status === 'delivered' ? (
						<div className='glass-card p-4 flex flex-col items-center gap-2 text-white/80'>
							<Truck className='w-6 h-6 text-[#00E676]' />
							<span className='text-sm font-bold'>Delivered</span>
						</div>
					) : (
						<button
							type='button'
							onClick={(e) => {
								e.preventDefault();
								navigate(`/agent/delivery-form/${billNo}`);
							}}
							className='glass-card p-4 flex flex-col items-center gap-2 text-white'>
							<Truck className='w-6 h-6 text-[#FFC107]' />
							<span className='text-sm font-bold'>Deliver Bill</span>
						</button>
					)}
				</div>

				{/* Map Modal - Only for Treatment 2 */}
				{showMap && hasLocation && (
					<div className='fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4'>
						<div className='bg-[#001E3C] rounded-2xl p-6 w-full max-w-md'>
							<div className='flex justify-between items-center mb-4'>
								<h3 className='text-xl font-black text-white'>Property Location</h3>
								<button
									type='button'
									onClick={(e) => {
										e.preventDefault();
										setShowMap(false);
									}}
									className='text-white/60 hover:text-white'>
									✕
								</button>
							</div>
							<div className='bg-white/5 rounded-xl p-4 mb-4'>
								<p className='text-sm text-white/60 mb-2'>Location:</p>
								<p className='text-white font-bold mb-4'>{hideImported(billData.location)}</p>
								<p className='text-sm text-white/60 mb-2'>GPS Coordinates:</p>
								<p className='text-white font-mono'>{billData.gpsLat}, {billData.gpsLng}</p>
							</div>
							{/* Google Maps Integration */}
							<div className='bg-white/5 rounded-xl h-64 mb-4 overflow-hidden'>
								<iframe
									width='100%'
									height='100%'
									style={{ border: 0 }}
									loading='lazy'
									allowFullScreen
									referrerPolicy='no-referrer-when-downgrade'
									src={`https://www.google.com/maps?q=${billData.gpsLat},${billData.gpsLng}&output=embed&zoom=15`}
									title='Property Location'>
								</iframe>
							</div>
							<button
								type='button'
								onClick={(e) => {
									e.preventDefault();
									// Open in external map app
									const url = `https://www.google.com/maps?q=${billData.gpsLat},${billData.gpsLng}`;
									window.open(url, '_blank');
								}}
								className='w-full py-3 rounded-xl btn-gold font-black uppercase text-sm'>
								Open in Maps
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default PropertyDetails;
